import { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { isEqual } from 'lodash-es';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import type { TypeTurnDetectionConfig, TypeTurnDetectionConfigForm } from '@intone/shared/types';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfigResetButtons } from '@/components/ConfigResetButtons';
import {
  FormCheckboxField,
  FormRangeField,
  FormSliderField,
} from '@/components/form-fields';

import { useTurnDetectionConfigs, useSaveTurnDetectionConfig } from '../model/useTurnDetectionConfigs';

interface TurnDetectionConfigurationBlockProps {
  agentId: string;
  selectedId: number;
  onHasChanges?: (hasChanges: boolean) => void;
  readOnly?: boolean;
  headerRight?: React.ReactNode;
}

export interface TurnDetectionConfigurationBlockRef {
  save: () => Promise<boolean>;
  hasChanges: () => boolean;
}

const FIELD_DESCRIPTIONS = {
  model_type: 'Language model for turn detection (e.g., "english", "multilingual")',
  endpointing_delay: 'Wait time after user stopped talking — processing won\'t begin for this time. Exact value defined by AI using Aggressiveness parameter',
  response_delay: 'Time limits for the agent to start-stop responding',
  interruption_threshold: 'How long (seconds) user must speak to trigger an interruption of agent speech',
  turn_taking_aggressiveness: '0.0 = passive (waits longer), 1.0 = aggressive (jumps in quickly)',
  allow_first_phrase_interruptions: 'Allow user to interrupt the agent during the first phrase of agent speech',
  backoff_on_interruption: 'If the agent is interrupted, wait for extra time before continuing to talk',
};

const mapToForm = (config?: TypeTurnDetectionConfig): TypeTurnDetectionConfigForm => ({
  name: config?.name ?? 'New Turn Detection Config',
  model_type: config?.model_type ?? 'english',
  unlikely_threshold: config?.unlikely_threshold ?? null,
  min_endpointing_delay: config?.min_endpointing_delay ?? 0.1,
  max_endpointing_delay: config?.max_endpointing_delay ?? 1.2,
  min_response_delay: config?.min_response_delay ?? 0.1,
  max_response_delay: config?.max_response_delay ?? 0.5,
  interruption_threshold: config?.interruption_threshold ?? 0.5,
  turn_taking_aggressiveness: config?.turn_taking_aggressiveness ?? 0.5,
  allow_first_phrase_interruptions: config?.allow_first_phrase_interruptions ?? true,
  min_endpointing_delay_on_interruption: config?.min_endpointing_delay_on_interruption ?? null,
  min_time_for_interruption_endpointing_delay: config?.min_time_for_interruption_endpointing_delay ?? null,
});

export const TurnDetectionConfigurationBlock = forwardRef<
  TurnDetectionConfigurationBlockRef,
  TurnDetectionConfigurationBlockProps
>(function TurnDetectionConfigurationBlock({ agentId, selectedId, onHasChanges, readOnly, headerRight }, ref) {
  const queryClient = useQueryClient();
  const { data: turnDetectionConfigs, isPending, isFetching, isSuccess } = useTurnDetectionConfigs(selectedId);
  const saveConfig = useSaveTurnDetectionConfig();

  const [currentConfig, setCurrentConfig] = useState<TypeTurnDetectionConfigForm>(mapToForm());

  const defaultConfig = useMemo(
    () => mapToForm(turnDetectionConfigs?.find(c => c.id === 1)),
    [turnDetectionConfigs]
  );

  const selectedConfig = useMemo(
    () => mapToForm(turnDetectionConfigs?.find(c => c.id === selectedId)),
    [turnDetectionConfigs, selectedId]
  );

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setCurrentConfig(selectedConfig);
    }
  }, [selectedId, isSuccess, isFetching]);

  const backoffEnabled = currentConfig.min_endpointing_delay_on_interruption != null
    && currentConfig.min_time_for_interruption_endpointing_delay != null;

  const handleBackoffToggle = (checked: boolean) => {
    setCurrentConfig((prev) => ({
      ...prev,
      min_endpointing_delay_on_interruption: checked ? 0.5 : null,
      min_time_for_interruption_endpointing_delay: checked ? 1.5 : null,
    }));
  };

  const hasChanges = useMemo(() => !isEqual(currentConfig, selectedConfig), [currentConfig, selectedConfig]);

  useEffect(() => {
    onHasChanges?.(hasChanges);
  }, [hasChanges, onHasChanges]);

  const updateConfig = <K extends keyof TypeTurnDetectionConfigForm>(
    field: K,
    value: TypeTurnDetectionConfigForm[K]
  ) => {
    setCurrentConfig((prev) => ({ ...prev, [field]: value }));
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!hasChanges) return true;

      if (!currentConfig.name) {
        toast.error('Turn Detection: Config name is required');
        return false;
      }

      if (currentConfig.min_endpointing_delay > currentConfig.max_endpointing_delay) {
        toast.error('Turn Detection: Min Endpointing Delay cannot be greater than Max');
        return false;
      }

      if (currentConfig.min_response_delay > currentConfig.max_response_delay) {
        toast.error('Turn Detection: Min Response Delay cannot be greater than Max');
        return false;
      }

      return new Promise((resolve) => {
        saveConfig.mutate(
          { id: selectedId === 1 ? undefined : selectedId, agentId, ...currentConfig },
          {
            onSuccess: () => {
              if (selectedId === 1) {
                queryClient.invalidateQueries({ queryKey: ['engineAgent', agentId] });
              }
              resolve(true);
            },
            onError: () => {
              resolve(false);
            },
          }
        );
      });
    },
    hasChanges: () => hasChanges,
  }), [hasChanges, currentConfig, selectedId, agentId, saveConfig, queryClient]);

  if (isPending) {
    return null;
  }

  return (
    <Card className={`border-gray-300 ${isFetching ? 'opacity-70' : ''}`}>
      <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-body font-semibold">Turn Detection Configuration</CardTitle>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <ConfigResetButtons
                hasChanges={hasChanges}
                showRestore={selectedId !== 1}
                isRestoreDisabled={isEqual(currentConfig, defaultConfig)}
                onReset={() => setCurrentConfig(selectedConfig)}
                onRestoreDefault={() => setCurrentConfig(defaultConfig)}
              />
            )}
            {headerRight}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 grid gap-4">
        <div className={`space-y-4 ${readOnly ? 'opacity-70 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="td-model-type" className="font-medium text-gray-900">Model Type<sup className="text-destructive">*</sup></Label>
              <Select
                value={currentConfig.model_type}
                onValueChange={(value: string) => updateConfig('model_type', value)}
              >
                <SelectTrigger id="td-model-type" className="h-8">
                  <SelectValue placeholder="Select model type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="multilingual">Multilingual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div />

            <FormRangeField
              id="td-response-delay"
              label="Response Delay"
              description={FIELD_DESCRIPTIONS.response_delay}
              minValue={currentConfig.min_response_delay}
              maxValue={currentConfig.max_response_delay}
              onMinChange={(v) => updateConfig('min_response_delay', v)}
              onMaxChange={(v) => updateConfig('max_response_delay', v)}
              rangeMin={0}
              rangeMax={3}
              step={0.05}
            />

            <FormSliderField
              id="td-interruption"
              label="Interruption Threshold"
              description={FIELD_DESCRIPTIONS.interruption_threshold}
              value={currentConfig.interruption_threshold}
              onChange={(v) => updateConfig('interruption_threshold', v)}
              rangeMin={0}
              rangeMax={2.5}
              step={0.05}
            />

            <FormRangeField
              id="td-endpointing-delay"
              label="Endpointing Delay"
              description={FIELD_DESCRIPTIONS.endpointing_delay}
              minValue={currentConfig.min_endpointing_delay}
              maxValue={currentConfig.max_endpointing_delay}
              onMinChange={(v) => updateConfig('min_endpointing_delay', v)}
              onMaxChange={(v) => updateConfig('max_endpointing_delay', v)}
              rangeMin={0}
              rangeMax={3}
              step={0.05}
            />

            <FormSliderField
              id="td-aggressiveness"
              label="Turn Taking Aggressiveness"
              description={FIELD_DESCRIPTIONS.turn_taking_aggressiveness}
              value={currentConfig.turn_taking_aggressiveness}
              onChange={(v) => updateConfig('turn_taking_aggressiveness', v)}
              rangeMin={0}
              rangeMax={1}
              step={0.05}
            />

            <FormCheckboxField
              id="td-allow-interrupt"
              label="Allow First Phrase Interruptions"
              description={FIELD_DESCRIPTIONS.allow_first_phrase_interruptions}
              checked={currentConfig.allow_first_phrase_interruptions}
              onChange={(v) => updateConfig('allow_first_phrase_interruptions', v)}
            />

            <FormCheckboxField
              id="td-enable-backoff"
              label="Enable backoff on interruption"
              description={FIELD_DESCRIPTIONS.backoff_on_interruption}
              checked={backoffEnabled}
              onChange={handleBackoffToggle}
            />
          </div>

          {backoffEnabled && (
            <div className="grid grid-cols-2 gap-4 ml-6">
              <FormSliderField
                id="td-backoff-delay"
                label="Backoff Delay"
                description="Extra wait time (seconds) before agent continues"
                value={currentConfig.min_endpointing_delay_on_interruption ?? 0}
                onChange={(v) => updateConfig('min_endpointing_delay_on_interruption', v)}
                rangeMin={0}
                rangeMax={3}
                step={0.05}
              />
              <FormSliderField
                id="td-backoff-threshold"
                label="Max Interruption Duration"
                description="Max interruption length (seconds) to trigger backoff"
                value={currentConfig.min_time_for_interruption_endpointing_delay ?? 0}
                onChange={(v) => updateConfig('min_time_for_interruption_endpointing_delay', v)}
                rangeMin={0}
                rangeMax={3}
                step={0.05}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});
